import type { PulseChart, SectionMarker } from '@shared/chart';
import { comboMultiplier } from '@shared/scoring';
import type { ParticleEmission } from './pulseRenderTypes';

/**
 * Hex → rgba string. Accepts 3-digit shorthand and 6-digit hex. Safe
 * against malformed input — bad channels parse as 0 rather than
 * NaN-poisoning the output rgba string.
 */
export function hexToRgba(hex: string, alpha: number): string {
  let h = hex.replace('#', '');
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('');
  }
  const r = parseInt(h.slice(0, 2), 16) || 0;
  const g = parseInt(h.slice(2, 4), 16) || 0;
  const b = parseInt(h.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Particle / floater default colour table. Spawners can override on
 * a per-emission basis (e.g. milestone star vs cup), but the table
 * carries the failure / success vocabulary:
 *
 *   • perfect → mint green (success)
 *   • good → cyan (mid)
 *   • miss / death → red (failure)
 *   • flip → magenta (player colour)
 *   • milestone → gold (celebratory)
 */
export function colourForEmission(type: ParticleEmission['type']): string {
  switch (type) {
    case 'hit_perfect':
      return '#3bff9d';
    case 'hit_good':
      return '#16e0ff';
    case 'hit_miss':
      return '#ff3b5c';
    case 'flip':
      return '#ff2bd6';
    case 'death':
      return '#ff3b5c';
    case 'milestone':
      return '#ffd84d';
    default:
      return '#ffffff';
  }
}

/**
 * Visual tier for the always-visible combo multiplier readout. Each
 * step up is a quiet colour/glow shift — never a notification — so
 * the player picks up "I just earned a higher multiplier"
 * peripherally without star/cup-style overlays competing with the run.
 *
 * Tiers mirror `comboMultiplier()` thresholds (×1 → ×1.25 → ×1.5 →
 * ×2 at combo 8 / 24 / 64) and add a muted ×1 state at combo 0 so
 * the baseline sits at the lowest visual weight on the HUD. Each
 * multiplier value gets its own hue so the player can read tier
 * from peripheral colour alone: grey (idle) → white-on-cyan (warming)
 * → cyan (×1.25) → pink (×1.5) → gold (×2, matches milestone
 * particle gold).
 */
export function comboHudTier(
  combo: number,
): { colour: string; glowColour: string; glow: number } {
  const multiplier = comboMultiplier(combo);
  if (multiplier >= 2) {
    return { colour: '#ffd84d', glowColour: '#ffd84d', glow: 24 };
  }
  if (multiplier >= 1.5) {
    return { colour: '#ff2bd6', glowColour: '#ff2bd6', glow: 18 };
  }
  if (multiplier >= 1.25) {
    return { colour: '#16e0ff', glowColour: '#16e0ff', glow: 12 };
  }
  if (combo > 0) {
    return { colour: '#e8ecff', glowColour: '#16e0ff', glow: 6 };
  }
  return { colour: '#8e96c7', glowColour: '#16e0ff', glow: 0 };
}

/**
 * Section-band tint for the background overlay. Verses + unknown
 * sections return `null` so the renderer skips the band entirely —
 * we only want to mark structural moments (chorus / drop / bridge),
 * not paint every section.
 */
export function sectionTint(sec: SectionMarker, palette: PulseChart['palette']): string | null {
  switch (sec.kind) {
    case 'chorus':
      return palette.padPerfect;
    case 'drop':
      return palette.player;
    case 'bridge':
      return palette.pad;
    case 'verse':
      return null;
    default:
      return null;
  }
}
